if (!customElements.get('bundle-products')) {
  customElements.define('bundle-products', class BundleProducts extends HTMLElement {
    constructor() {
      super();
      this.onBundleChange = this.onBundleChange.bind(this);
      this.onProductFormChange = this.onProductFormChange.bind(this);
      this.onProductFormSubmit = this.onProductFormSubmit.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.bundleInitialized === 'true') return;
      this.dataset.bundleInitialized = 'true';

      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.querySelector('[data-bundle-submit]');
      this.buttonText = this.querySelector('[data-bundle-button-text]');
      this.priceEl = this.querySelector('[data-bundle-total]');
      this.spinner = this.querySelector('[data-bundle-spinner]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.defaultButtonLabel = this.dataset.buttonLabel || 'Add Selected Items';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productData = this.getProductData();

      if (!this.form || !this.button || !this.priceEl || !this.spinner || !this.productData) return;

      this.variantInput = this.form.querySelector('[name="id"]');
      this.quantityInput = this.form.querySelector('[name="quantity"]');

      this.addEventListener('change', this.onBundleChange);
      this.form.addEventListener('change', this.onProductFormChange);
      this.form.addEventListener('input', this.onProductFormChange);
      this.form.addEventListener('submit', this.onProductFormSubmit, true);
      this.button.addEventListener('click', this.onButtonClick);

      this.updateUi();
    }

    disconnectedCallback() {
      if (!this.form || !this.button) return;
      this.removeEventListener('change', this.onBundleChange);
      this.form.removeEventListener('change', this.onProductFormChange);
      this.form.removeEventListener('input', this.onProductFormChange);
      this.form.removeEventListener('submit', this.onProductFormSubmit, true);
      this.button.removeEventListener('click', this.onButtonClick);
    }

    getProductData() {
      const productJson = document.getElementById(`ProductJson-${this.sectionId}`);
      if (!productJson) return null;

      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        console.error('Bundle products JSON parse error:', error);
        return null;
      }
    }

    getCurrentVariant() {
      if (!this.variantInput) return null;
      const variantId = parseInt(this.variantInput.value, 10);
      if (!variantId || !this.productData || !this.productData.variants) return null;
      return this.productData.variants.find((variant) => variant.id === variantId) || null;
    }

    getQuantity() {
      if (!this.quantityInput) return 1;
      const quantity = parseInt(this.quantityInput.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getSelectedBundleItems() {
      return Array.from(this.querySelectorAll('.m-ctl__cb:checked')).map((checkbox) => ({
        id: parseInt(checkbox.value, 10),
        price: parseInt(checkbox.dataset.price, 10) || 0,
        available: checkbox.dataset.available === 'true'
      })).filter((item) => item.id && item.available);
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }

    buildLineItems() {
      const variant = this.getCurrentVariant();
      if (!variant || !variant.available) return [];

      const quantity = this.getQuantity();
      const lineItems = [{ id: variant.id, quantity }];

      this.getSelectedBundleItems().forEach((item) => {
        lineItems.push({ id: item.id, quantity });
      });

      return this.mergeLineItems(lineItems);
    }

    mergeLineItems(items) {
      const merged = new Map();

      items.forEach((item) => {
        if (!merged.has(item.id)) {
          merged.set(item.id, { id: item.id, quantity: 0 });
        }

        merged.get(item.id).quantity += item.quantity;
      });

      return Array.from(merged.values());
    }

    updateUi() {
      const variant = this.getCurrentVariant();
      const quantity = this.getQuantity();
      const bundleTotal = this.getSelectedBundleItems().reduce((sum, item) => sum + item.price, 0);
      const mainPrice = variant ? variant.price : 0;
      const total = (mainPrice + bundleTotal) * quantity;
      const variantAvailable = Boolean(variant && variant.available);

      this.button.disabled = !variantAvailable;
      this.button.setAttribute('aria-disabled', String(!variantAvailable));
      this.buttonText.textContent = variantAvailable ? this.defaultButtonLabel : this.soldOutLabel;
      this.priceEl.textContent = total > 0 ? ' - ' + this.formatMoney(total) : '';
    }

    async onButtonClick(event) {
      event.preventDefault();
      await this.submitBundle();
    }

    onBundleChange(event) {
      if (event.target.classList.contains('m-ctl__cb')) {
        this.updateUi();
      }
    }

    onProductFormChange() {
      this.updateUi();
    }

    onProductFormSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.button.getAttribute('aria-busy') === 'true') return;
      this.submitBundle();
    }

    async submitBundle() {
      const lineItems = this.buildLineItems();
      if (!lineItems.length) return;

      this.setLoading(true);

      try {
        const response = await this.addBundleItems(lineItems);

        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Cart add failed');
        }

        if (!this.cartDrawer) {
          window.location = routes.cart_url;
          return;
        }

        this.cartDrawer.setActiveElement(this.button);
        if (payload.sections && payload.sections['cart-drawer']) {
          this.cartDrawer.renderContents(payload);
        } else {
          const sections = await this.fetchCartDrawerSections();
          this.cartDrawer.renderContents({
            id: lineItems[0].id,
            sections: {
              'cart-drawer': sections['cart-drawer']
            }
          });
        }

        const cart = await this.fetchCart();
        this.updateHeaderCount(cart);

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: cart
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: cart
        }));

        if (typeof freeShippMsg === 'function') {
          try {
            freeShippMsg();
          } catch (error) {
            console.warn('freeShippMsg failed after bundle add', error);
          }
        }
      } catch (error) {
        console.error('Bundle add error:', error);
        alert(error.message || 'Something went wrong. Please try again.');
      } finally {
        this.setLoading(false);
        this.updateUi();
      }
    }

    async addBundleItems(lineItems) {
      const formData = new FormData();

      lineItems.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });

      if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
        this.cartDrawer.getSectionsToRender().forEach((section) => {
          formData.append('sections', section.id);
        });
        formData.append('sections_url', window.location.pathname);
      }

      return fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
    }

    async fetchCartDrawerSections() {
      const response = await fetch(window.location.pathname + '?sections=cart-drawer', {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Cart drawer refresh failed');
      }

      return response.json();
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Cart refresh failed');
      }

      return response.json();
    }

    updateHeaderCount(cart) {
      document.querySelectorAll('#CartCount, .site-header__cart-count, [data-cart-count], [data-cart-item-count]').forEach((element) => {
        element.textContent = cart.item_count;
      });
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading || this.button.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(isLoading));
      this.buttonText.style.display = isLoading ? 'none' : '';
      this.priceEl.style.display = isLoading ? 'none' : '';
      this.spinner.style.display = isLoading ? 'block' : 'none';
    }
  });
}
